import { useCallback, useMemo } from 'react';

import { type CombinedError } from 'urql';

import {
  type ApplyRewardFailureCode,
  type ApplyRewardResponseUnion,
  type RemoveRewardResponseUnion,
  type RewardNotApplied,
  type ValidateRewardFailureCode,
} from '@sg/graphql-schema';

import {
  useDaysOfTheWeekMessages,
  useFallbackRewardErrorMessagesByCode,
  useFallbackRewardErrorMessagesByReason,
  useFallbackRewardGenericErrorMessages,
} from './hooks';
import {
  type ApplyRewardErrorCode,
  type ApplyRewardErrorMessages,
} from './useRewardErrorMessage.constants';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps apply and remove reward responses to error messages from Contentful (with intl fallbacks).
 *
 * @see {@link https://app.contentful.com/spaces/wme4s8lvzccr/entries/12qSE321S24oul9rg2VXnx}
 */
export const useRewardErrorMessage = (params: Params) => {
  const { messages } = params;

  // ─── Messages ─────────────────────────────────────────────────────────

  const daysOfTheWeek = useDaysOfTheWeekMessages();

  const fallbackGenericMessages = useFallbackRewardGenericErrorMessages();
  const fallbackMessagesByCode = useFallbackRewardErrorMessagesByCode();
  const fallbackMessagesByReason = useFallbackRewardErrorMessagesByReason();

  const defaultErrorMessage =
    messages?.REWARD_NOT_APPLICABLE ?? fallbackMessagesByCode.REWARD_NOT_APPLICABLE;
  const networkErrorMessage = messages?.NETWORK_ERROR ?? fallbackGenericMessages.NETWORK_ERROR;
  const multipleErrorsMessage =
    messages?.REWARD_MULTIPLE_ERRORS ?? fallbackGenericMessages.REWARD_MULTIPLE_ERRORS;
  const unauthorizedOrderModificationMessage =
    messages?.UNAUTHORIZED_ORDER_MODIFICATION ??
    fallbackGenericMessages.UNAUTHORIZED_ORDER_MODIFICATION;
  const orderLockedMessage = messages?.ORDER_LOCKED ?? fallbackGenericMessages.ORDER_LOCKED;
  const cartNotFoundMessage = messages?.CART_NOT_FOUND ?? fallbackGenericMessages.CART_NOT_FOUND;
  const rewardNotRemovedMessage = fallbackGenericMessages.REWARD_NOT_REMOVED;

  const blockedRewardErrorMessages = useMemo<BlockedRewardErrorMessages>(
    () => ({
      unauthorizedOrderModification: unauthorizedOrderModificationMessage,
      orderLocked: orderLockedMessage,
      cartNotFound: cartNotFoundMessage,
    }),
    [unauthorizedOrderModificationMessage, orderLockedMessage, cartNotFoundMessage],
  );

  // ─── Helpers ──────────────────────────────────────────────────────────

  const getApplyRewardFailureCode = useCallback(
    (code: ApplyRewardFailureCode) => {
      const messageFromContentful = messages?.[code as ApplyRewardErrorCode];
      const fallbackMessage = fallbackMessagesByCode[code];

      return messageFromContentful ?? fallbackMessage ?? defaultErrorMessage;
    },
    [defaultErrorMessage, fallbackMessagesByCode, messages],
  );

  const getApplyRewardFailureReason = useCallback(
    (reason: ValidateRewardFailureCode) => {
      const messageFromContentful = messages?.[reason as ApplyRewardErrorCode];
      const fallbackMessage = fallbackMessagesByReason[reason];

      return messageFromContentful ?? fallbackMessage ?? defaultErrorMessage;
    },
    [defaultErrorMessage, fallbackMessagesByReason, messages],
  );

  // ─── Apply ────────────────────────────────────────────────────────────

  const getApplyRewardErrorMessage = useCallback(
    (
      data: RewardNotApplied | { __typename?: ApplyRewardTypename } | null | undefined,
      error: CombinedError | undefined,
    ) => {
      const isOtherError = data?.__typename !== 'RewardNotApplied';

      if (error || !data) {
        return networkErrorMessage;
      }

      const blockedRewardErrorMessage = getBlockedRewardErrorMessage(
        data.__typename,
        blockedRewardErrorMessages,
      );

      if (blockedRewardErrorMessage) {
        return blockedRewardErrorMessage;
      }

      if (isOtherError) {
        return defaultErrorMessage;
      }

      // ─────────────────────────────────────────────────────────────────────

      const errorData = data as RewardNotApplied;
      const { failureCode, failureReasons, failureMetadata } = errorData;

      const firstFailureReason = failureReasons[0];

      const hasMultipleFailureReasons = failureReasons.length > 1;
      const hasValidFailureReason = typeof firstFailureReason === 'string';
      const hasSingleFailureReason = failureReasons.length === 1 && hasValidFailureReason; // prettier-ignore

      const requiredChannel = failureMetadata.requiredChannel ?? '';
      const requiredDays: readonly number[] = failureMetadata.requiredDays ?? []; // prettier-ignore

      // ─────────────────────────────────────────────────────────────────────

      if (hasMultipleFailureReasons) {
        return multipleErrorsMessage;
      }

      if (hasSingleFailureReason) {
        const days = getDays(requiredDays, daysOfTheWeek);
        const failureReason = firstFailureReason as ValidateRewardFailureCode;
        const message = getApplyRewardFailureReason(failureReason);

        return message.replaceAll('(channel)', requiredChannel).replaceAll('(days)', days); // prettier-ignore
      }

      if (failureCode) {
        return getApplyRewardFailureCode(failureCode);
      }

      return defaultErrorMessage;
    },
    [
      defaultErrorMessage,
      networkErrorMessage,
      getApplyRewardFailureCode,
      multipleErrorsMessage,
      blockedRewardErrorMessages,
      getApplyRewardFailureReason,
      daysOfTheWeek,
    ],
  );

  // ─── Remove ───────────────────────────────────────────────────────────

  const getRemoveRewardErrorMessage = useCallback(
    (data: { __typename?: RemoveRewardTypename } | null | undefined) =>
      getBlockedRewardErrorMessage(data?.__typename, blockedRewardErrorMessages) ??
      rewardNotRemovedMessage,
    [blockedRewardErrorMessages, rewardNotRemovedMessage],
  );

  // ─────────────────────────────────────────────────────────────────────

  return { getApplyRewardErrorMessage, getRemoveRewardErrorMessage };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Given an array of Loyalty vendor days, return the localized representation for it.
 */
function getDays(
  requiredDays: readonly number[],
  daysOfTheWeek: ReturnType<typeof useDaysOfTheWeekMessages>,
): string {
  if (requiredDays.length === 0) {
    return '';
  }

  return requiredDays.map((day) => daysOfTheWeek[day]).filter(Boolean).join(', '); // prettier-ignore
}

/**
 * Returns the message for responses blocked by group order guards.
 */
function getBlockedRewardErrorMessage(
  typename: ApplyRewardTypename | RemoveRewardTypename | undefined,
  messages: BlockedRewardErrorMessages,
) {
  if (typename === 'UnauthorizedOrderModification') {
    return messages.unauthorizedOrderModification;
  }

  if (typename === 'OrderLocked') {
    return messages.orderLocked;
  }

  if (typename === 'CartNotFound') {
    return messages.cartNotFound;
  }

  return undefined;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  messages: ApplyRewardErrorMessages | undefined;
};

type ApplyRewardTypename = ApplyRewardResponseUnion['__typename'];

type RemoveRewardTypename = RemoveRewardResponseUnion['__typename'];

type BlockedRewardErrorMessages = {
  unauthorizedOrderModification: string;
  orderLocked: string;
  cartNotFound: string;
};
